import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface PersonOption {
  id: string;
  label: string;
}

interface Props {
  options: PersonOption[];
  value: string;
  onChange: (id: string) => void;
  /** Що показати, коли нічого не обрано. */
  placeholder?: string;
  /** Додати рядок «—», яким вибір скидають. */
  clearLabel?: string;
  /** Не тримати вибране в полі: обрав — і поле знову порожнє («+ Кому…»). */
  action?: boolean;
  disabled?: boolean;
  style?: CSSProperties;
}

const norm = (s: string): string => s.toLowerCase().replace(/[’']/g, '');

/** Поле вибору людини з пошуком: пишеш — список звужується. */
export function PersonSelect({
  options,
  value,
  onChange,
  placeholder = 'Оберіть людину',
  clearLabel,
  action = false,
  disabled,
  style,
}: Props): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const [box, setBox] = useState<{ left: number; top: number; width: number } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.id === value);
  const shown = useMemo(() => {
    const words = norm(query).split(/\s+/).filter(Boolean);
    const found = words.length
      ? options.filter(o => {
          const hay = norm(o.label);
          return words.every(w => hay.includes(w));
        })
      : options;
    return clearLabel && !words.length ? [{ id: '', label: clearLabel }, ...found] : found;
  }, [options, query, clearLabel]);

  const close = (): void => {
    setOpen(false);
    setQuery('');
  };
  const pick = (id: string): void => {
    onChange(id);
    close();
  };

  useLayoutEffect(() => {
    if (!open || !wrap.current) return;
    const place = (): void => {
      const r = wrap.current!.getBoundingClientRect();
      setBox({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (wrap.current?.contains(t) || list.current?.contains(t)) return;
      close();
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query, open]);

  useEffect(() => {
    list.current?.querySelector('[data-on="1"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const onKey = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setCursor(c => Math.min(c + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      const o = shown[cursor];
      if (o) pick(o.id);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    }
  };

  return (
    <div className="psel" ref={wrap} style={style}>
      <input
        className="input psel-input"
        type="text"
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        disabled={disabled}
        placeholder={open && selected && !action ? selected.label : action ? placeholder : selected ? undefined : placeholder}
        value={open ? query : action ? '' : (selected?.label ?? '')}
        onFocus={e => {
          setOpen(true);
          e.currentTarget.select();
        }}
        onClick={() => setOpen(true)}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKey}
      />
      {open && box
        ? createPortal(
            <div
              ref={list}
              className="psel-list"
              style={{ left: box.left, top: box.top, minWidth: box.width }}
              role="listbox"
            >
              {shown.length ? (
                shown.map((o, i) => (
                  <div
                    key={o.id || '__none'}
                    role="option"
                    aria-selected={o.id === value}
                    data-on={i === cursor ? '1' : undefined}
                    className={`psel-opt${i === cursor ? ' on' : ''}${o.id === value && !action ? ' cur' : ''}`}
                    onMouseEnter={() => setCursor(i)}
                    onMouseDown={e => {
                      e.preventDefault();
                      pick(o.id);
                    }}
                  >
                    {o.label}
                  </div>
                ))
              ) : (
                <div className="psel-empty">Нікого не знайдено</div>
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
